import { CustomerPortalRpcClient } from "@redotech/customer-portal-sdk/rpc/client";
import { useHandler } from "@redotech/react-util/hook";
import { useLoad, useTriggerLoad } from "@redotech/react-util/load";
import { ReturnAppSettings } from "@redotech/redo-customer-portal-shared/settings";
import { DraftReturn } from "@redotech/redo-model/draft-return/draft-return";
import { calculateOfferings } from "@redotech/redo-model/extended-warranties/calculate-offerings";
import ShieldTickIcon from "@redotech/redo-web/arbiter-icon/shield-tick.svg";
import { CurrencyContext } from "@redotech/redo-web/currency";
import { Flex } from "@redotech/redo-web/flex";
import { LoadingRedoAnimation } from "@redotech/redo-web/loading-redo-animation";
import { Text } from "@redotech/redo-web/text";
import { CardElement } from "@stripe/react-stripe-js";
import { loadStripe, Stripe, StripeElements } from "@stripe/stripe-js";
import { memo, useContext, useMemo, useState } from "react";
import { User } from "../user";
import { RegistrationDetails } from "./components/submit-warranty-registration";
import { EWPaymentCard } from "./review-components/ew-payment-card";
import { ReviewPageHeader } from "./review-components/review-page-header";
import * as review from "./review.module.css";
import {
  cleanResponses,
  getWarrantyRegistrationData,
  useSubmitWarrantyRegistration,
} from "./use-submit-warranty-registration";

export const RegistrationReview = memo(function RegistrationReview({
  draftReturn,
  client,
  settings,
  user,
  setDraftReturn,
}: {
  draftReturn: DraftReturn;
  client: CustomerPortalRpcClient;
  settings: ReturnAppSettings;
  user?: User;
  setDraftReturn: (draftReturn: DraftReturn) => void;
}) {
  const { formatCurrency } = useContext(CurrencyContext);
  const [stripe, setStripe] = useState<Stripe | undefined>(undefined);
  const [elements, setElements] = useState<StripeElements | undefined>(
    undefined,
  );
  const stripeLoad = useLoad(async () => {
    if (stripe || !client) {
      return;
    }
    const { publicKey } = await client.getStripePublicKey({});
    const loadedStripe = await loadStripe(publicKey);
    if (loadedStripe) {
      setStripe(loadedStripe);
    }
  }, [client]);

  const { responses, userInfo, offeringSelection, optInMarketing } =
    getWarrantyRegistrationData(draftReturn.pendingReturnItems[0]);

  const { submitAndNavigateToWarrantyRegistration } =
    useSubmitWarrantyRegistration({ setDraftReturn });

  const selectedOffering = useMemo(() => {
    const product = draftReturn.returnableItems[0].products[0];
    const collectionOfferings = settings.extendedWarranties?.collectionSettings;

    if (!collectionOfferings) {
      return undefined;
    }

    // TODO: If product collections included handle, we would not need to recalculate offerings
    const offerings = calculateOfferings({
      collectionOfferings,
      variant: {
        // Expects price to be in cents
        price: Number(product.price) * 100,
        variantId: product.variantId,
        collections: product.collections.map((collection) => ({
          title: collection,
          // TODO: Make return product collections include handle
          handle: "unknown",
        })),
      },
    });
    return offerings?.find(
      (offering) => offering.id === offeringSelection?.timeId,
    );
  }, [offeringSelection, draftReturn, settings]);

  const [submitLoad, submit] = useTriggerLoad(async (_) => {
    let token: string | null = null;
    const card = elements?.getElement(CardElement);
    if (!stripe || !card) {
      // setCardError(
      //   "We expected payment information but none was provided, please contact Redo support.",
      // );
      return;
    }

    const { error, paymentMethod } = await stripe.createPaymentMethod({
      type: "card",
      card,
      billing_details: { name: draftReturn.customer.name },
    });
    if (error) {
      console.error(error);
      // setCardError(error.message ?? "");
      return;
    } else {
      token = paymentMethod.id;
    }

    await submitAndNavigateToWarrantyRegistration({
      product: draftReturn.returnableItems[0].products[0],
      account: userInfo,
      responses: cleanResponses(responses),
      draftReturnId: draftReturn._id,
      offeringSelection,
      paymentToken: token,
      notifyCustomer: true,
      optInMarketing,
    });
  });

  const handleSubmit = useHandler(async () => {
    submit();
  });

  const products = draftReturn.returnableItems.map((item) => item.products[0]);

  if (stripeLoad.pending || !stripe || !selectedOffering) {
    return <LoadingRedoAnimation />;
  }

  return (
    <>
      <ReviewPageHeader
        displayHeaderOnly={false}
        draftReturn={draftReturn}
        headerText="Review your registration"
        settings={settings}
      />
      <div className={review.contentWrapper}>
        <div className={review.leftContent}>
          <div className={review.subheader}>What you're registering</div>
          <div className={review.cards}>
            <div className={review.card}>
              {products.map((product, index) => (
                <div className={review.flex} key={index}>
                  <CardImage
                    alt={product.variantData?.variants?.[0]?.title || ""}
                    src={product.image}
                  />
                  <div className={review.cardItems}>
                    <div className={review.cardItemTitle}>
                      <div className={review.title}>{product?.title}</div>
                      <div className={review.variantTitle}>
                        Quantity: {product.quantity ?? 1}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div className={review.card}>
              <div className={review.subheader}>Extended warranty</div>
              <Flex align="center" py="xl">
                <ShieldTickIcon className={review.shieldIcon} />
                <Text>
                  {`${selectedOffering.title} protection plan for ${formatCurrency(selectedOffering.price.amount ?? 0)}`}
                </Text>
              </Flex>
            </div>
            <div className={review.subheader}>Registration details</div>
            <RegistrationDetails responses={responses} userInfo={userInfo} />
          </div>
        </div>
        <div className={review.summaryCards}>
          <EWPaymentCard
            elements={elements}
            selectedOffering={selectedOffering}
            setElements={setElements}
            settings={settings}
            stripe={stripe}
            submit={handleSubmit}
            submitPending={submitLoad.pending}
          />
        </div>
      </div>
    </>
  );
});

const CardImage = memo(function CardImage(props: {
  src?: string;
  alt?: string;
}) {
  if (!props.src) {
    return null;
  }
  return <img alt={props.alt} className={review.cardImage} src={props.src} />;
});
